/**
 * Git CLI operations for GitHub integration
 */

import { spawn } from 'child_process';
import { promises as fs } from 'fs';
import { join } from 'path';
import { IntegrationError } from '../../types';
import type {
  GitHubCloneParams,
  GitHubCreateBranchParams,
  GitHubCommitParams,
  GitHubPushParams,
  GitHubInitParams,
  GitHubPullParams,
  GitHubPullResult,
  GitHubAddRemoteParams,
  GitHubSetUserConfigParams,
  GitHubRenameBranchParams,
} from './types';
import type { GitHubCommit } from './github-api';

/**
 * Execute a git command
 */
async function execGit(
  args: string[],
  cwd: string,
  env?: Record<string, string>
): Promise<{ stdout: string; stderr: string }> {
  return new Promise((resolve, reject) => {
    const proc = spawn('git', args, {
      cwd,
      env: { ...process.env, ...env },
      stdio: 'pipe',
    });

    let stdout = '';
    let stderr = '';

    proc.stdout?.on('data', (data) => {
      stdout += data.toString();
    });

    proc.stderr?.on('data', (data) => {
      stderr += data.toString();
    });

    proc.on('close', (code) => {
      if (code === 0) {
        resolve({ stdout, stderr });
      } else {
        reject(
          new IntegrationError(
            `Git command failed: ${args.join(' ')}\n${stderr}`,
            'SERVICE_ERROR',
            { code, stderr }
          )
        );
      }
    });

    proc.on('error', (error) => {
      reject(
        new IntegrationError(
          `Failed to execute git: ${error.message}`,
          'SERVICE_ERROR',
          { error }
        )
      );
    });
  });
}

/**
 * Clone a repository
 */
export async function cloneRepo(
  params: GitHubCloneParams,
  token: string
): Promise<void> {
  const { repoUrl, targetDir, branch, depth = 1 } = params;

  // Inject token into URL for authentication
  const authUrl = injectTokenIntoUrl(repoUrl, token);

  const args = ['clone'];

  if (depth > 0) {
    args.push('--depth', depth.toString());
  }

  if (branch) {
    args.push('--branch', branch);
  }

  args.push(authUrl, targetDir);

  try {
    await execGit(args, process.cwd());

    // Immediately scrub the token from git config
    await scrubTokenFromRemote(targetDir, repoUrl);
  } catch (error) {
    throw new IntegrationError(
      `Failed to clone repository: ${error instanceof Error ? error.message : String(error)}`,
      'SERVICE_ERROR',
      { repoUrl, error }
    );
  }
}

/**
 * Create a new branch
 */
export async function createBranch(
  params: GitHubCreateBranchParams,
  workDir: string
): Promise<void> {
  const { branchName, baseBranch } = params;
  const cwd = params.workDir || workDir;

  try {
    // If baseBranch is specified, check it out first
    if (baseBranch) {
      await execGit(['checkout', baseBranch], cwd);
    }

    // Create and checkout new branch
    await execGit(['checkout', '-b', branchName], cwd);
  } catch (error) {
    throw new IntegrationError(
      `Failed to create branch: ${error instanceof Error ? error.message : String(error)}`,
      'SERVICE_ERROR',
      { branchName, baseBranch, error }
    );
  }
}

/**
 * Commit changes
 */
export async function commit(
  params: GitHubCommitParams,
  workDir: string
): Promise<void> {
  const { message, files } = params;
  const cwd = params.workDir || workDir;

  try {
    // Stage files
    if (files && files.length > 0) {
      await execGit(['add', ...files], cwd);
    } else {
      // Stage all changes
      await execGit(['add', '.'], cwd);
    }

    // Commit
    await execGit(['commit', '-m', message], cwd);
  } catch (error) {
    throw new IntegrationError(
      `Failed to commit: ${error instanceof Error ? error.message : String(error)}`,
      'SERVICE_ERROR',
      { message, error }
    );
  }
}

/**
 * Push branch to remote
 */
export async function push(
  params: GitHubPushParams,
  workDir: string,
  token: string
): Promise<void> {
  const { branchName, force = false } = params;
  const cwd = params.workDir || workDir;

  // Security: Never allow force push to protected branches
  const protectedBranches = ['main', 'master', 'production', 'prod'];
  if (force && protectedBranches.includes(branchName.toLowerCase())) {
    throw new IntegrationError(
      `Force push to protected branch '${branchName}' is not allowed`,
      'VALIDATION_ERROR'
    );
  }

  try {
    // Create a temporary credential helper script
    const credHelper = await createTempCredentialHelper(token);

    const args = ['push'];

    if (force) {
      args.push('--force');
    }

    args.push('-u', 'origin', branchName);

    // Use GIT_ASKPASS for authentication
    await execGit(args, cwd, {
      GIT_ASKPASS: credHelper,
      GIT_TERMINAL_PROMPT: '0',
    });

    // Clean up credential helper
    await fs.unlink(credHelper);
  } catch (error) {
    throw new IntegrationError(
      `Failed to push: ${error instanceof Error ? error.message : String(error)}`,
      'SERVICE_ERROR',
      { branchName, error }
    );
  }
}

/**
 * Inject token into repository URL for authentication
 */
function injectTokenIntoUrl(repoUrl: string, token: string): string {
  const url = new URL(repoUrl);
  url.username = 'x-access-token';
  url.password = token;
  return url.toString();
}

/**
 * Remove token from git remote URL (security measure)
 */
async function scrubTokenFromRemote(workDir: string, originalUrl: string): Promise<void> {
  try {
    // Parse the URL to ensure it's clean (no credentials)
    const url = new URL(originalUrl);
    url.username = '';
    url.password = '';
    const cleanUrl = url.toString();

    await execGit(['remote', 'set-url', 'origin', cleanUrl], workDir);
  } catch (error) {
    // Log but don't fail - the clone succeeded
    console.error('Warning: Failed to scrub token from remote URL:', error);
  }
}

/**
 * Create a temporary credential helper script for GIT_ASKPASS
 */
async function createTempCredentialHelper(token: string): Promise<string> {
  const tmpDir = process.env.TMPDIR || '/tmp';
  const helperPath = join(tmpDir, `git-cred-${Date.now()}.sh`);

  const script = `#!/bin/sh\necho "${token}"`;

  await fs.writeFile(helperPath, script, { mode: 0o700 });

  return helperPath;
}

/**
 * Get current branch name
 */
export async function getCurrentBranch(workDir: string): Promise<string> {
  try {
    const { stdout } = await execGit(['branch', '--show-current'], workDir);
    return stdout.trim();
  } catch (error) {
    throw new IntegrationError(
      `Failed to get current branch: ${error instanceof Error ? error.message : String(error)}`,
      'SERVICE_ERROR',
      { error }
    );
  }
}

/**
 * Check if working directory has uncommitted changes
 */
export async function hasUncommittedChanges(workDir: string): Promise<boolean> {
  try {
    const { stdout } = await execGit(['status', '--porcelain'], workDir);
    return stdout.trim().length > 0;
  } catch (error) {
    throw new IntegrationError(
      `Failed to check git status: ${error instanceof Error ? error.message : String(error)}`,
      'SERVICE_ERROR',
      { error }
    );
  }
}

/**
 * Initialize a git repository in `workDir`. No-op when `.git/` already
 * exists. The default initial branch is `main` to match what GitHub
 * creates for new repos.
 */
export async function init(params: GitHubInitParams): Promise<void> {
  const { workDir, initialBranch = 'main' } = params;
  try {
    // `git init -b <branch>` works on git >= 2.28; the runtime ships 2.x.
    // The command is idempotent — re-running on an existing repo just
    // re-asserts the initial branch ref (no error).
    await execGit(['init', '-b', initialBranch], workDir);
  } catch (error) {
    throw new IntegrationError(
      `Failed to git init: ${error instanceof Error ? error.message : String(error)}`,
      'SERVICE_ERROR',
      { workDir, error }
    );
  }
}

/**
 * Add a remote to the repository. Used after `init` to attach the
 * freshly-created GitHub remote as `origin`.
 */
export async function addRemote(params: GitHubAddRemoteParams): Promise<void> {
  const { name, url, workDir } = params;
  const cwd = workDir || params.workDir;
  if (!cwd) {
    throw new IntegrationError(
      `addRemote requires workDir`,
      'VALIDATION_ERROR',
      { params }
    );
  }
  try {
    await execGit(['remote', 'add', name, url], cwd);
  } catch (error) {
    throw new IntegrationError(
      `Failed to add remote ${name}: ${error instanceof Error ? error.message : String(error)}`,
      'SERVICE_ERROR',
      { name, error }
    );
  }
}

/**
 * Set `user.name` and `user.email` for git commits. Scope defaults to
 * per-repo (`--local`) so each workspace can carry the GitHub identity
 * of the user who owns it — global config would clobber the developer's
 * own machine settings.
 */
export async function setUserConfig(params: GitHubSetUserConfigParams): Promise<void> {
  const { userName, userEmail, scope = 'local', workDir } = params;
  const scopeFlag = scope === 'global' ? '--global' : '--local';
  if (scope === 'local' && !workDir) {
    throw new IntegrationError(
      `setUserConfig with scope='local' requires workDir`,
      'VALIDATION_ERROR',
      { params }
    );
  }
  const cwd = workDir ?? process.cwd();
  try {
    await execGit(['config', scopeFlag, 'user.name', userName], cwd);
    await execGit(['config', scopeFlag, 'user.email', userEmail], cwd);
  } catch (error) {
    throw new IntegrationError(
      `Failed to set git user config: ${error instanceof Error ? error.message : String(error)}`,
      'SERVICE_ERROR',
      { error }
    );
  }
}

/**
 * Pull from the remote. Returns a structured outcome so callers can
 * tell "the workspace advanced" from "nothing changed because <X>"
 * without parsing git's stderr. Best-effort posture: network / conflict
 * / no-upstream all resolve cleanly rather than throwing — the caller
 * decides whether to surface the no-op as user-facing.
 */
export async function pull(
  params: GitHubPullParams,
  token: string,
): Promise<GitHubPullResult> {
  const { branchName, ffOnly = true, workDir } = params;
  const cwd = workDir || params.workDir;
  if (!cwd) {
    return { updated: false, reason: 'no-upstream' };
  }

  let credHelper: string | null = null;
  try {
    credHelper = await createTempCredentialHelper(token);
    const args = ['pull'];
    if (ffOnly) args.push('--ff-only');
    args.push('origin', branchName);

    const { stdout } = await execGit(args, cwd, {
      GIT_ASKPASS: credHelper,
      GIT_TERMINAL_PROMPT: '0',
    });
    // `git pull` prints "Already up to date." when there's nothing to
    // fetch. Anything else (Updating, Fast-forward, +n -m line) means
    // the working tree actually moved.
    const updated = !/^Already up to date\.?\s*$/m.test(stdout.trim());
    return updated ? { updated: true } : { updated: false, reason: 'up-to-date' };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    // Heuristics on git's stderr text — exit codes are too coarse to
    // discriminate the cases.
    if (/no upstream|no such ref|couldn't find remote ref/i.test(message)) {
      return { updated: false, reason: 'no-upstream' };
    }
    if (/conflict|not possible to fast-forward|divergent/i.test(message)) {
      return { updated: false, reason: 'conflict' };
    }
    return { updated: false, reason: 'network' };
  } finally {
    if (credHelper) {
      try { await fs.unlink(credHelper); } catch { /* best-effort */ }
    }
  }
}

/**
 * Return the current HEAD commit sha (full 40-char). Throws when the
 * workspace has no commits yet (caller decides whether that's an error
 * or an "init, then commit, then read" sequencing issue).
 */
export async function getHeadSha(workDir: string): Promise<string> {
  try {
    const { stdout } = await execGit(['rev-parse', 'HEAD'], workDir);
    return stdout.trim();
  } catch (error) {
    throw new IntegrationError(
      `Failed to get HEAD sha: ${error instanceof Error ? error.message : String(error)}`,
      'SERVICE_ERROR',
      { error }
    );
  }
}

/**
 * Return the current HEAD commit as a `GitHubCommit` shape. Reuses the
 * canonical type from the REST module (no parallel `LocalCommit`
 * interface). `stats` is absent — that's GitHub-API-only and would
 * require a separate fetch to populate.
 *
 * The format string uses NUL (`%x00`) as a field separator so commit
 * messages containing newlines / pipes can't break parsing.
 */
export async function getHeadCommit(workDir: string): Promise<GitHubCommit> {
  try {
    const FORMAT = '%H%x00%an%x00%ae%x00%aI%x00%B';
    const { stdout } = await execGit(
      ['show', '--no-patch', `--format=${FORMAT}`, 'HEAD'],
      workDir,
    );
    const [sha, name, email, date, ...messageParts] = stdout.split('\x00');
    return {
      sha: sha.trim(),
      message: messageParts.join('\x00').trimEnd(),
      author: {
        name: name?.trim() ?? '',
        email: email?.trim() ?? '',
        date: date?.trim() ?? '',
      },
    };
  } catch (error) {
    throw new IntegrationError(
      `Failed to read HEAD commit: ${error instanceof Error ? error.message : String(error)}`,
      'SERVICE_ERROR',
      { error }
    );
  }
}

/**
 * Rename the current branch with `git branch -M <newName>`. The `-M`
 * (capital) flag forces the rename even if a branch with that name
 * already exists, which is fine for our use case (aligning a fresh
 * local workspace's branch with the linked repo's default branch).
 */
export async function renameCurrentBranch(params: GitHubRenameBranchParams): Promise<void> {
  const { newName, workDir } = params;
  try {
    await execGit(['branch', '-M', newName], workDir);
  } catch (error) {
    throw new IntegrationError(
      `Failed to rename branch to ${newName}: ${error instanceof Error ? error.message : String(error)}`,
      'SERVICE_ERROR',
      { newName, error }
    );
  }
}
